import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import authServiceDefault from '../services/authService';
import { adminService } from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

const PASSWORD_POLICY_MESSAGE = 'Password must be at least 10 characters long and include at least one letter and one number.';

const TABS = {
  password: 'password',
  security: 'security',
  maintenance: 'maintenance',
};

const tabStyles = {
  container: {
    display: 'flex',
    gap: 0,
    borderBottom: '2px solid #e0e0e0',
    marginBottom: '2rem',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s ease',
  },
  tabActive: {
    color: 'var(--company-primary-color, #273947)',
    fontWeight: 600,
    borderBottomColor: 'var(--company-primary-color, #273947)',
  },
};

/* ─── Recovery Codes Panel (for 2FA) ─── */
function RecoveryCodesPanel({ codes }) {
  if (!codes.length) return null;
  return (
    <div style={{ marginTop: '1rem', padding: '1rem', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px' }}>
      <h3 style={{ marginTop: 0 }}>Recovery Codes</h3>
      <p style={{ color: '#6b5a00', marginBottom: '1rem' }}>
        Save these one-time recovery codes in a secure place. Each code can be used once if you lose access to your authenticator app.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
        {codes.map((code) => (
          <div key={code} style={{ fontFamily: 'monospace', background: 'white', padding: '0.75rem', borderRadius: '6px', border: '1px solid #f0d76d', textAlign: 'center' }}>
            {code}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Change Password Tab ─── */
function ChangePasswordTab() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useModal();
  const homePath = user?.role === 'superadmin' ? '/admin/companies' : '/admin/templates';
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      await showAlert('Please fill in all fields.', 'Validation');
      return;
    }
    if (newPassword !== confirmPassword) {
      await showAlert('New password and confirmation do not match.', 'Validation');
      return;
    }
    if (!/(?=.*[A-Za-z])(?=.*\d).{10,}/.test(newPassword)) {
      await showAlert(PASSWORD_POLICY_MESSAGE, 'Validation');
      return;
    }
    try {
      setSaving(true);
      await authService.changePassword(currentPassword, newPassword);
      await showAlert('Password changed successfully!', 'Success');
      navigate(homePath);
    } catch (error) {
      console.error('Failed to change password:', error);
      await showAlert(error.response?.data?.error || 'Failed to change password. Please check your current password and try again.', 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '500px' }}>
      <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', padding: '2rem' }}>
        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>Change Password</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 10 characters, include letters and numbers)"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
            <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#6b7280' }}>{PASSWORD_POLICY_MESSAGE}</div>
          </div>
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' }}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => navigate(homePath)}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: saving ? '#9ca3af' : 'var(--company-primary-color, #273947)', color: 'var(--company-primary-contrast, #fff)', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
            >
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Security (2FA) Tab ─── */
function SecurityTab() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ enabled: false, enabledAt: null });
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [setupCode, setSetupCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenCode, setRegenCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const qrPreviewUrl = useMemo(() => {
    if (!setupData?.qrCodeUri) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(setupData.qrCodeUri)}`;
  }, [setupData]);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await authServiceDefault.getTwoFactorStatus();
      setStatus(response);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load two-factor status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const resetMessages = () => { setMessage(''); setError(''); };

  const handleStartSetup = async () => {
    resetMessages();
    setSubmitting(true);
    try {
      const response = await authServiceDefault.beginTwoFactorSetup();
      setSetupData(response);
      setRecoveryCodes([]);
      setSetupCode('');
      setMessage('Scan the QR code with Microsoft Authenticator or enter the manual key.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start two-factor setup.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      const response = await authServiceDefault.confirmTwoFactorSetup(setupCode);
      setRecoveryCodes(response.recoveryCodes || []);
      setSetupData(null);
      setSetupCode('');
      setStatus({ enabled: true, enabledAt: new Date().toISOString() });
      setMessage('Two-factor authentication is now enabled.');
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to confirm two-factor setup.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      await authServiceDefault.disableTwoFactor(disablePassword, disableCode);
      setDisablePassword('');
      setDisableCode('');
      setSetupData(null);
      setRecoveryCodes([]);
      setStatus({ enabled: false, enabledAt: null });
      setMessage('Two-factor authentication has been disabled.');
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable two-factor authentication.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegenerateRecoveryCodes = async (e) => {
    e.preventDefault();
    resetMessages();
    setSubmitting(true);
    try {
      const response = await authServiceDefault.regenerateRecoveryCodes(regenCode);
      setRecoveryCodes(response.recoveryCodes || []);
      setRegenCode('');
      setMessage('Recovery codes regenerated successfully.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to regenerate recovery codes.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div style={{ background: 'white', borderRadius: '10px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h2 style={{ marginTop: 0, fontSize: '1.25rem', fontWeight: 600 }}>Two-Factor Authentication</h2>
        <p style={{ color: '#666' }}>
          Protect your administrator account with a time-based code from Microsoft Authenticator or another compatible authenticator app.
        </p>

        {loading ? <p>Loading security status...</p> : (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '999px', background: status.enabled ? '#e8f5e9' : '#f5f5f5', color: status.enabled ? '#2e7d32' : '#616161', fontWeight: 600 }}>
              <span>{status.enabled ? 'Enabled' : 'Disabled'}</span>
              {status.enabledAt ? <span style={{ fontWeight: 400 }}>since {new Date(status.enabledAt).toLocaleString()}</span> : null}
            </div>

            {message ? <div className="alert" style={{ marginTop: '1rem', background: '#e8f5e9', color: '#2e7d32', padding: '0.75rem 1rem', borderRadius: '6px' }}>{message}</div> : null}
            {error ? <div className="alert alert-error" style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '6px' }}>{error}</div> : null}

            {!status.enabled && !setupData ? (
              <div style={{ marginTop: '1.5rem' }}>
                <button
                  onClick={handleStartSetup}
                  disabled={submitting}
                  style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: 'var(--company-primary-color, #273947)', color: 'var(--company-primary-contrast, #fff)', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}
                >
                  {submitting ? 'Preparing...' : 'Set Up Two-Factor Authentication'}
                </button>
              </div>
            ) : null}

            {setupData ? (
              <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#fafafa', border: '1px solid #eee', borderRadius: '8px' }}>
                <h3 style={{ marginTop: 0 }}>Step 1: Add account to authenticator app</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
                  <div>
                    {qrPreviewUrl ? (
                      <img src={qrPreviewUrl} alt="Two-factor QR code" style={{ width: 220, height: 220, border: '1px solid #ddd', borderRadius: '8px', background: 'white' }} />
                    ) : null}
                  </div>
                  <div style={{ flex: 1, minWidth: '260px' }}>
                    <p style={{ marginTop: 0 }}>If scanning does not work, enter this key manually:</p>
                    <div style={{ fontFamily: 'monospace', fontSize: '1rem', padding: '0.75rem', background: 'white', border: '1px solid #ddd', borderRadius: '6px', wordBreak: 'break-all' }}>
                      {setupData.manualEntryKey}
                    </div>
                    <form onSubmit={handleConfirmSetup} style={{ marginTop: '1rem' }}>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label htmlFor="setupCode" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Step 2: Enter the 6-digit code shown in the app</label>
                        <input
                          id="setupCode"
                          type="text"
                          value={setupCode}
                          onChange={(e) => setSetupCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                          required
                          disabled={submitting}
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="123456"
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                        <button type="submit" disabled={submitting} style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: 'var(--company-primary-color, #273947)', color: 'var(--company-primary-contrast, #fff)', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}>
                          {submitting ? 'Confirming...' : 'Confirm and Enable'}
                        </button>
                        <button
                          type="button"
                          disabled={submitting}
                          onClick={() => { setSetupData(null); setSetupCode(''); resetMessages(); }}
                          style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                        >
                          Cancel Setup
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            ) : null}

            {status.enabled ? (
              <>
                <form onSubmit={handleRegenerateRecoveryCodes} style={{ marginTop: '2rem', padding: '1.5rem', background: '#fafafa', border: '1px solid #eee', borderRadius: '8px' }}>
                  <h3 style={{ marginTop: 0 }}>Regenerate Recovery Codes</h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label htmlFor="regenCode" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Authenticator code</label>
                    <input
                      id="regenCode"
                      type="text"
                      value={regenCode}
                      onChange={(e) => setRegenCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      required
                      disabled={submitting}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      style={{ width: '100%', maxWidth: '300px', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button type="submit" disabled={submitting} style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 500 }}>
                    Regenerate Recovery Codes
                  </button>
                </form>

                <form onSubmit={handleDisable} style={{ marginTop: '2rem', padding: '1.5rem', background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '8px' }}>
                  <h3 style={{ marginTop: 0, color: '#b42318' }}>Disable Two-Factor Authentication</h3>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label htmlFor="disablePassword" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Current password</label>
                    <input
                      id="disablePassword"
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      required
                      disabled={submitting}
                      style={{ width: '100%', maxWidth: '300px', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label htmlFor="disableCode" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Authenticator code or recovery code</label>
                    <input
                      id="disableCode"
                      type="text"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.trim())}
                      required
                      disabled={submitting}
                      placeholder="123456 or 1234-5678"
                      style={{ width: '100%', maxWidth: '300px', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <button type="submit" disabled={submitting} style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', background: '#dc3545', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}>
                    Disable Two-Factor Authentication
                  </button>
                </form>
              </>
            ) : null}

            <RecoveryCodesPanel codes={recoveryCodes} />
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Maintenance Tab ─── */
function MaintenanceTab() {
  const { showAlert, showConfirm } = useModal();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [message, setMessage] = useState('System maintenance in progress. Please try again later.');

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const data = await adminService.getMaintenanceSchedule();
      setSchedule(data);
    } catch (err) {
      console.error('Failed to load maintenance schedule:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSchedule(); }, []);

  // Convert datetime-local value (treated as HKT / UTC+8) to ISO string
  const hktToISO = (datetimeLocalValue) => {
    // datetime-local gives "YYYY-MM-DDTHH:mm", treat as HKT (UTC+8)
    return new Date(datetimeLocalValue + '+08:00').toISOString();
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!startTime || !endTime) {
      await showAlert('Please set both start and end times.', 'Validation');
      return;
    }
    const startISO = hktToISO(startTime);
    const endISO = hktToISO(endTime);
    const start = new Date(startISO);
    const end = new Date(endISO);
    if (end <= start) {
      await showAlert('End time must be after start time.', 'Validation');
      return;
    }
    if (end < new Date()) {
      await showAlert('End time must be in the future (Hong Kong Time).', 'Validation');
      return;
    }
    try {
      setSaving(true);
      await adminService.setMaintenanceSchedule({
        startTime: startISO,
        endTime: endISO,
        message: message || undefined,
      });
      await showAlert('Maintenance scheduled successfully!', 'Success');
      setStartTime('');
      setEndTime('');
      await loadSchedule();
    } catch (err) {
      await showAlert(err.response?.data?.error || 'Failed to schedule maintenance.', 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    const confirmed = await showConfirm('Are you sure you want to cancel the scheduled maintenance?', {
      title: 'Cancel Maintenance',
      confirmText: 'Yes, Cancel',
      cancelText: 'No',
    });
    if (!confirmed) return;
    try {
      await adminService.cancelMaintenance();
      await showAlert('Maintenance schedule cancelled.', 'Success');
      await loadSchedule();
    } catch (err) {
      await showAlert('Failed to cancel maintenance.', 'Error');
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'Asia/Hong_Kong',
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }) + ' (HKT)';
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Active', bg: '#fee2e2', color: '#991b1b' },
      scheduled: { label: 'Scheduled', bg: '#fef9c3', color: '#854d0e' },
    };
    const badge = badges[status] || { label: 'None', bg: '#dcfce7', color: '#166534' };
    return (
      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, background: badge.bg, color: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const inputStyle = { width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#333' };

  // Compute min datetime for date pickers using Hong Kong time (UTC+8)
  const pad = (n) => String(n).padStart(2, '0');
  const nowHK = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Hong_Kong' }));
  nowHK.setSeconds(0, 0); // Round to nearest minute
  const minDateTime = `${nowHK.getFullYear()}-${pad(nowHK.getMonth() + 1)}-${pad(nowHK.getDate())}T${pad(nowHK.getHours())}:${pad(nowHK.getMinutes())}`;
  // End time must be after start time (or current time if no start set)
  const minEndTime = startTime || minDateTime;

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: '600px' }}>
      {/* Schedule Form */}
      <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', padding: '2rem', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: '0 0 1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>Schedule Maintenance</h2>
        <form onSubmit={handleSchedule}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Start Time</label>
            <input type="datetime-local" value={startTime} onChange={e => setStartTime(e.target.value)} min={minDateTime} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>End Time</label>
            <input type="datetime-local" value={endTime} onChange={e => setEndTime(e.target.value)} min={minEndTime} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '2rem' }}>
            <label style={labelStyle}>Maintenance Message</label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Message shown to patients during maintenance..."
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: saving ? '#9ca3af' : 'var(--company-primary-color, #273947)', color: 'var(--company-primary-contrast, #fff)', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600' }}
          >
            {saving ? 'Scheduling...' : 'Schedule Maintenance'}
          </button>
        </form>
      </div>

      {/* Current Schedule */}
      {schedule?.hasSchedule && (
        <div style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', padding: '2rem' }}>
          <h2 style={{ margin: '0 0 1rem', fontSize: '1.25rem', fontWeight: 600 }}>Current Schedule</h2>
          <div style={{ marginBottom: '1rem' }}>
            <span style={{ fontWeight: 500, color: '#555', marginRight: '0.5rem' }}>Status:</span>
            {getStatusBadge(schedule.status)}
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontWeight: 500, color: '#555' }}>Start: </span>
            <span>{formatDateTime(schedule.startTime)}</span>
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontWeight: 500, color: '#555' }}>End: </span>
            <span>{formatDateTime(schedule.endTime)}</span>
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <span style={{ fontWeight: 500, color: '#555' }}>Message: </span>
            <span style={{ color: '#666' }}>{schedule.message}</span>
          </div>
          {schedule.createdBy && (
            <div style={{ marginBottom: '1.5rem', fontSize: '13px', color: '#999' }}>
              Scheduled by {schedule.createdBy} at {formatDateTime(schedule.createdAt)}
            </div>
          )}
          <button
            onClick={handleCancel}
            style={{ padding: '0.75rem 1.5rem', borderRadius: '6px', border: '1px solid #ef4444', backgroundColor: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
          >
            Cancel Maintenance
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main Settings Page ─── */
export default function ChangePasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin';

  // Determine active tab from URL path
  const activeTab = location.pathname === '/admin/security'
    ? TABS.security
    : location.pathname === '/admin/maintenance'
      ? TABS.maintenance
      : TABS.password;

  const switchTab = (tab) => {
    if (tab === TABS.security) {
      navigate('/admin/security', { replace: true });
    } else if (tab === TABS.maintenance) {
      navigate('/admin/maintenance', { replace: true });
    } else {
      navigate('/admin/change-password', { replace: true });
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '2rem' }}>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>Settings</h1>

        {/* Tab Navigation */}
        <div style={tabStyles.container}>
          <button
            style={{ ...tabStyles.tab, ...(activeTab === TABS.password ? tabStyles.tabActive : {}) }}
            onClick={() => switchTab(TABS.password)}
          >
            Change Password
          </button>
          <button
            style={{ ...tabStyles.tab, ...(activeTab === TABS.security ? tabStyles.tabActive : {}) }}
            onClick={() => switchTab(TABS.security)}
          >
            Security (2FA)
          </button>
          {isAdmin && (
            <button
              style={{ ...tabStyles.tab, ...(activeTab === TABS.maintenance ? tabStyles.tabActive : {}) }}
              onClick={() => switchTab(TABS.maintenance)}
            >
              Maintenance
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === TABS.maintenance && isAdmin
          ? <MaintenanceTab />
          : activeTab === TABS.security
            ? <SecurityTab />
            : <ChangePasswordTab />
        }
      </div>
    </div>
  );
}
